import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'

import { groupValidationSchema } from '../groupValidationSchema'

import { ConfirmationResultModal, FormField, TextInput, TextArea, Spacer } from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import type { EditGroupModalProps } from './EditGroupModal.types'

type ModalState = 'confirm' | 'success' | 'failure'

export function EditGroupModal({
  isOpen,
  onClose,
  groupId,
  groupName,
  groupDescription,
  onSuccess
}: EditGroupModalProps) {
  const { t } = useTranslation()
  const { groupService } = useServices()
  const [modalState, setModalState] = useState<ModalState>('confirm')

  const [{ isPending }, { mutateAsync: editGroup }] = useMutation(groupService.editGroup)

  const initialValues = useMemo(
    () => ({
      name: groupName,
      description: groupDescription ?? ''
    }),
    [groupName, groupDescription]
  )

  const formik = useFormik({
    initialValues,
    validationSchema: groupValidationSchema(t),
    enableReinitialize: true,
    onSubmit: async values => {
      try {
        await editGroup({
          id: groupId,
          name: values.name.trim(),
          description: values.description.trim() || null
        })
        setModalState('success')
      } catch {
        setModalState('failure')
      }
    }
  })

  const hasChanges = useMemo(() => {
    const nameChanged = formik.values.name.trim() !== initialValues.name
    const descriptionChanged = formik.values.description.trim() !== (initialValues.description ?? '')

    return nameChanged || descriptionChanged
  }, [formik.values, initialValues])

  const handleClose = () => {
    if (!isPending) {
      formik.resetForm()
      setModalState('confirm')
      onClose()
    }
  }

  const handleSuccessClose = () => {
    formik.resetForm()
    setModalState('confirm')
    onSuccess?.()
    onClose()
  }

  const handleFailureClose = () => {
    setModalState('confirm')
  }

  if (!isOpen) {
    return null
  }

  const isSubmitDisabled = !formik.values.name.trim() || !formik.isValid || !hasChanges || isPending

  return (
    <ConfirmationResultModal
      isOpen={isOpen}
      state={modalState === 'confirm' ? undefined : modalState}
      title={t('modal.editGroup.title')}
      primaryButtonText={isPending ? t('modal.editGroup.button.saving') : t('button.saveChanges')}
      secondaryButtonText={t('button.cancel')}
      onConfirm={formik.handleSubmit}
      onClose={modalState === 'success' ? handleSuccessClose : handleClose}
      onFailureClose={handleFailureClose}
      disabled={isSubmitDisabled}
      loading={isPending}
      primaryBtnTestId="edit-group-submit-button"
      secondaryBtnTestId="edit-group-cancel-button"
      success={{
        title: t('modal.editGroup.success.title'),
        description: t('modal.editGroup.success.description'),
        button: t('button.close')
      }}
      failure={{
        title: t('modal.editGroup.error.title'),
        description: t('modal.editGroup.error.description'),
        button: t('button.close')
      }}
    >
      <form>
        <FormField
          label={t('modal.editGroup.name.label')}
          description={t('modal.editGroup.name.hint')}
          status={formik.touched.name && formik.errors.name ? 'danger' : 'default'}
          statusMessage={formik.touched.name && formik.errors.name ? formik.errors.name : ''}
          necessity="required"
        >
          <TextInput
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            data-testid="edit-group-name-input"
          />
        </FormField>
        <Spacer size={4} axis="vertical" />
        <FormField label={t('modal.editGroup.description.label')}>
          <TextArea
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            data-testid="edit-group-description-input"
          />
        </FormField>
      </form>
    </ConfirmationResultModal>
  )
}
